import type { Document, AnyNode } from "domhandler";
import { createUniqueId, type JSX, type JSXElement } from "solid-js";
import DOMPurify from "isomorphic-dompurify";
import { ElementType, parseDocument } from "htmlparser2";
import { Dynamic } from "solid-js/web";

export type HtmlProcessor = {
  clean(html: string): string;
  renderHtml(doc: string): JSXElement[];
};

export class HTMLProcessor implements HtmlProcessor {
  private withStyleTags: boolean;
  private cssContainmentClassName: string;

  public constructor(withStyleTags = false) {
    this.withStyleTags = withStyleTags;
    this.cssContainmentClassName = createUniqueId();
  }

  public clean(html: string): string {
    const parsedHtml = this.withStyleTags ? `<div class="${this.cssContainmentClassName}">${html}</div>` : html;

    return DOMPurify.sanitize(parsedHtml, {
      USE_PROFILES: { html: true },
      ADD_TAGS: ["iframe"],
      FORBID_TAGS: this.withStyleTags ? [] : ["style"],
    });
  }

  public renderHtml(html: string): JSX.Element[] {
    return this.process(html).children.map((child) => this.renderNode(child));
  }

  private process(html: string): Document {
    const doc = parseDocument(html, { decodeEntities: true });
    doc.children.forEach((child) => this.processNode(child));
    return doc;
  }

  private renderNode(node: AnyNode): JSX.Element {
    if (this.withStyleTags && node.type === ElementType.Style) {
      const cssContent = node.children
        .filter((child) => child.type === ElementType.Text)
        .map((child) => child.data)
        .join("");

      return Dynamic({
        component: "style",
        children: `.${this.cssContainmentClassName} {${cssContent}}`,
        ...node.attribs,
      });
    }

    if (node.type === ElementType.Tag) {
      const children = node.children.map((child) => this.renderNode(child));
      return Dynamic({ component: node.name, children, ...node.attribs });
    }

    if (node.type === ElementType.Text) {
      return node.data;
    }

    return null;
  }

  private processNode(node: AnyNode): void {
    this.transformNode(node);
    if (node.type === ElementType.Tag) {
      node.children.forEach((child) => this.processNode(child));
    }
  }

  private transformNode(node: AnyNode): void {
    if (node.type !== ElementType.Tag) {
      return;
    }

    switch (node.name) {
      case "h1":
        node.name = "h2";
        break;
      case "a":
        node.attribs.target = "_blank";
        node.attribs.rel = "noopener noreferrer";
        break;
    }
  }
}
